// src/services/productionExcel.ts
//
// Excel (xlsx) import + template download for the Production module.
// Covers:
//   - Forecast template + import (most common bulk-entry use case)
//   - Items template + import (raw materials with cost + onHand)
//
// Recipes and Products are NOT covered in this pass — they're multi-row
// per entity (components + packaging) which needs a multi-sheet workbook
// design. Tracked as a follow-up.
//
// Patterns borrowed from GeniBatch's excelImport.js (column synonym
// matching, defensive number parsing, sheet-to-rows helpers).

import * as XLSX from 'xlsx';
import type { ProductionItem, ProductionProduct, ForecastLine } from '../types/production';

// ─── Helpers ──────────────────────────────────────────────────────────────

const s = (v: unknown): string => (v == null ? '' : String(v)).trim();
const lc = (v: unknown): string => s(v).toLowerCase();

function numOf(v: unknown): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  const m = s(v).replace(/,/g, '').match(/-?\d+(?:\.\d+)?/);
  return m ? parseFloat(m[0]) : null;
}

function sheetRows(ws: XLSX.WorkSheet): unknown[][] {
  return XLSX.utils.sheet_to_json(ws, { header: 1, raw: true, defval: '' }) as unknown[][];
}

/**
 * Resolve column indexes by header synonyms. Exact match wins over partial,
 * longer match wins over shorter. Returns `-1` for missing columns so the
 * caller can validate required ones.
 */
function mapColumns(headerRow: unknown[], spec: Record<string, string[]>): Record<string, number> {
  const cells = (headerRow || []).map(lc);
  const out: Record<string, number> = {};
  for (const f of Object.keys(spec)) out[f] = -1;
  cells.forEach((cell, idx) => {
    if (!cell) return;
    let bestField: string | null = null;
    let bestScore = 0;
    for (const [field, synonyms] of Object.entries(spec)) {
      for (const syn of synonyms) {
        const score = cell === syn ? 100 + syn.length : (cell.includes(syn) ? syn.length : 0);
        if (score > bestScore) { bestScore = score; bestField = field; }
      }
    }
    if (bestField && out[bestField] === -1) out[bestField] = idx;
  });
  return out;
}

function writeBlob(wb: XLSX.WorkBook, filename: string): void {
  const out = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
  const blob = new Blob([out], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Forecast template + parser ───────────────────────────────────────────

const FORECAST_COLUMNS = {
  product:  ['product', 'product name', 'sku', 'item'],
  qty:      ['qty', 'quantity', 'units', 'count'],
  date:     ['date', 'target date', 'target_date', 'period'],
  notes:    ['notes', 'note', 'comment', 'comments'],
};

export function downloadForecastTemplate(productNames: string[] = []): void {
  const headers = ['Product', 'Qty', 'Target Date (optional)', 'Notes (optional)'];
  const examples: Array<Array<string | number>> = productNames.slice(0, 5).map((name, i) => [
    name, (i + 1) * 100, '', '',
  ]);
  if (examples.length === 0) {
    examples.push(['Example Product Name', 100, '2026-07-01', 'Replace with your products']);
  }
  const aoa = [headers, ...examples];
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!cols'] = [{ wch: 32 }, { wch: 12 }, { wch: 20 }, { wch: 40 }];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Forecast');
  writeBlob(wb, `production-forecast-template.xlsx`);
}

export interface ParsedForecastRow {
  productMatch: ProductionProduct | null;
  productLabel: string;
  qty: number;
  targetDate?: string;
  notes?: string;
  warning?: string;
}

export function parseForecastWorkbook(
  buf: ArrayBuffer,
  products: ProductionProduct[],
): { rows: ParsedForecastRow[]; warnings: string[] } {
  const data = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  const wb = XLSX.read(data, { type: 'array' });
  const sheetName = wb.SheetNames[0];
  if (!sheetName) return { rows: [], warnings: ['Workbook has no sheets.'] };
  const ws = wb.Sheets[sheetName];
  const rows = sheetRows(ws);
  if (rows.length === 0) return { rows: [], warnings: ['First sheet is empty.'] };

  const colMap = mapColumns(rows[0], FORECAST_COLUMNS);
  if (colMap.product === -1 || colMap.qty === -1) {
    return {
      rows: [],
      warnings: [`Could not find required columns. Need at least one of "${FORECAST_COLUMNS.product.join(' / ')}" AND one of "${FORECAST_COLUMNS.qty.join(' / ')}" in row 1.`],
    };
  }

  const productByName = new Map<string, ProductionProduct>();
  const productBySku  = new Map<string, ProductionProduct>();
  for (const p of products) {
    productByName.set(p.name.toLowerCase().trim(), p);
    if (p.sku) productBySku.set(p.sku.toLowerCase().trim(), p);
  }

  const parsed: ParsedForecastRow[] = [];
  const warnings: string[] = [];
  for (let r = 1; r < rows.length; r++) {
    const row = rows[r] || [];
    const label = s(row[colMap.product]);
    const qty = numOf(row[colMap.qty]);
    if (!label && (qty == null || qty === 0)) continue;
    if (!label) { warnings.push(`Row ${r + 1}: blank product, skipped.`); continue; }
    if (qty == null || qty <= 0) { warnings.push(`Row ${r + 1} (${label}): missing/invalid qty, skipped.`); continue; }

    const lookup = label.toLowerCase().trim();
    const match = productByName.get(lookup) ?? productBySku.get(lookup) ?? null;

    parsed.push({
      productMatch: match,
      productLabel: label,
      qty,
      targetDate: colMap.date  >= 0 ? s(row[colMap.date])  || undefined : undefined,
      notes:      colMap.notes >= 0 ? s(row[colMap.notes]) || undefined : undefined,
      warning: match ? undefined : 'no matching product (will be skipped on confirm)',
    });
  }
  return { rows: parsed, warnings };
}

export function applyForecastRows(parsed: ParsedForecastRow[]): Omit<ForecastLine, 'id'>[] {
  return parsed
    .filter((p) => p.productMatch)
    .map((p) => ({
      productId: p.productMatch!.id,
      qty: p.qty,
      targetDate: p.targetDate,
      notes: p.notes,
    }));
}

// ─── Items template + parser ──────────────────────────────────────────────

const ITEMS_COLUMNS = {
  name:     ['name', 'item', 'item name', 'description'],
  category: ['category', 'cat', 'group'],
  unit:     ['unit', 'uom', 'unit of measure'],
  cost:     ['cost', 'cost / unit', 'cost per unit', 'unit cost', 'price'],
  onHand:   ['on hand', 'onhand', 'on-hand', 'qty on hand', 'inventory', 'stock'],
  notes:    ['notes', 'note', 'description', 'desc'],
};

export function downloadItemsTemplate(existing: ProductionItem[] = []): void {
  const headers = ['Name', 'Category', 'Unit', 'Cost / unit', 'On hand (optional)', 'Notes (optional)'];
  const examples = existing.slice(0, 5).map((it) => [
    it.name, it.category || '', it.unit, it.costPerUnit, it.onHand ?? '', it.description || '',
  ]);
  if (examples.length === 0) {
    examples.push(['Example Item', 'Produce', 'lb', 1.99, 50, 'Replace with your items']);
  }
  const aoa: Array<Array<string | number>> = [headers, ...examples];
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!cols'] = [{ wch: 32 }, { wch: 18 }, { wch: 10 }, { wch: 12 }, { wch: 14 }, { wch: 40 }];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Items');
  writeBlob(wb, 'production-items-template.xlsx');
}

export interface ParsedItemRow {
  name: string;
  category?: string;
  unit: string;
  costPerUnit: number;
  onHand?: number;
  description?: string;
  warning?: string;
  existingId?: string;  // when an Item with this name already exists
}

export function parseItemsWorkbook(
  buf: ArrayBuffer,
  existing: ProductionItem[],
): { rows: ParsedItemRow[]; warnings: string[] } {
  const data = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  const wb = XLSX.read(data, { type: 'array' });
  const sheetName = wb.SheetNames[0];
  if (!sheetName) return { rows: [], warnings: ['Workbook has no sheets.'] };
  const ws = wb.Sheets[sheetName];
  const rows = sheetRows(ws);
  if (rows.length === 0) return { rows: [], warnings: ['First sheet is empty.'] };

  const colMap = mapColumns(rows[0], ITEMS_COLUMNS);
  if (colMap.name === -1 || colMap.unit === -1 || colMap.cost === -1) {
    return {
      rows: [],
      warnings: [`Could not find required columns. Need "Name", "Unit", and "Cost / unit" in row 1.`],
    };
  }

  const byName = new Map<string, ProductionItem>();
  for (const it of existing) byName.set(it.name.toLowerCase().trim(), it);

  const parsed: ParsedItemRow[] = [];
  const warnings: string[] = [];
  for (let r = 1; r < rows.length; r++) {
    const row = rows[r] || [];
    const name = s(row[colMap.name]);
    if (!name) continue;
    const cost = numOf(row[colMap.cost]);
    if (cost == null) {
      warnings.push(`Row ${r + 1} (${name}): missing/invalid cost, skipped.`);
      continue;
    }
    const unit = s(row[colMap.unit]) || 'ea';
    const onHand = colMap.onHand >= 0 ? numOf(row[colMap.onHand]) : null;
    const existingItem = byName.get(name.toLowerCase().trim());

    parsed.push({
      name,
      category: colMap.category >= 0 ? s(row[colMap.category]) || undefined : undefined,
      unit,
      costPerUnit: cost,
      onHand: onHand != null ? onHand : undefined,
      description: colMap.notes >= 0 ? s(row[colMap.notes]) || undefined : undefined,
      existingId: existingItem?.id,
      warning: existingItem ? 'will UPDATE existing item' : undefined,
    });
  }
  return { rows: parsed, warnings };
}
